import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import toastr from 'toastr';
import Swal from 'sweetalert2';
import { getToxReview, applyToxReview } from '../../../services/clinicPortalServices/orderSearchService';

const pill = (bg, color = '#fff') => ({ background: bg, color, borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', display: 'inline-block' });
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

export default function ToxReviewPopup({ orderId, onClose, onApplied }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [applying, setApplying] = useState(false);
    useEffect(() => {
        getToxReview(orderId)
            .then((res) => (res && res.status === 200 ? setData(res.data) : setError((res && res.message) || 'Could not load tox review')))
            .catch(() => setError('Could not load tox review'));
    }, [orderId]);

    return (
        <Modal show onHide={onClose} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title><i className="fas fa-flask mr-2"></i>Tox Result Review{data && data.lab_order_id ? ` — ${data.lab_order_id}` : ''}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <div className="alert alert-danger">{error}</div>}
                {!data && !error && <div className="text-center py-4"><span className="spinner-border spinner-border-sm mr-2"></span>Loading…</div>}
                {data && (
                    <>
                        <div className="d-flex flex-wrap mb-3" style={{ gap: 12 }}>
                            <div className="px-3 py-2 border rounded"><b>{data.summary.total}</b> analytes</div>
                            <div className="px-3 py-2 border rounded"><b>{data.summary.detected}</b> detected</div>
                            <div className="px-3 py-2 rounded" style={{ border: `2px solid ${data.summary.inconsistent ? '#dc3545' : '#28a745'}` }}>
                                <b style={{ color: data.summary.inconsistent ? '#dc3545' : '#28a745' }}>{data.summary.inconsistent}</b> inconsistent
                            </div>
                        </div>
                        {data.applied && (
                            <div className="alert alert-success py-2 mb-3" style={{ fontSize: 13 }}>
                                <i className="fas fa-check-circle mr-1"></i>
                                Applied to report {data.applied.applied_at ? new Date(data.applied.applied_at).toLocaleString() : ''} by {data.applied.applied_by}. Re-apply only if results or medications changed.
                            </div>
                        )}
                        <div className="mb-3">
                            <b>Patient medications: </b>
                            {data.medications.length ? data.medications.map((m, i) => (
                                <span key={i} className="mr-2" style={pill('#e7f1ff', '#0b5ed7')}>
                                    {m.name}{m.dosage ? ` ${m.dosage}${m.dosageUnit || ''}` : ''}{m.frequency ? ` ${m.frequency}` : ''}{m.matched_via ? <em style={{ opacity: 0.8 }}>{` · via "${m.matched_via}"`}</em> : ''}
                                </span>
                            )) : <span className="text-muted">None reported</span>}
                        </div>
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered" style={{ fontSize: 13 }}>
                                <thead className="table-light">
                                    <tr><th>Analyte</th><th>Screen (IA)</th><th>Confirmation (LC-MS/MS)</th><th>Cutoff</th><th>Detected</th><th>Prescribed</th><th>Outcome</th><th>Detection window</th></tr>
                                </thead>
                                <tbody>
                                    {data.rows.map((r, i) => (
                                        <tr key={i} style={r.outcome === 'INCONSISTENT' ? { background: '#fff5f5' } : null}>
                                            <td><b>{r.analyte}</b></td>
                                            <td>
                                                {r.screen ? <span style={pill(r.screen.positive ? '#fd7e14' : '#6c757d')}>{r.screen.positive ? 'POS' : 'NEG'}</span> : <span className="text-muted">—</span>}
                                                {r.screen && <small className="text-muted ml-1">{r.screen.result} (≥{r.screen.cutoff})</small>}
                                            </td>
                                            <td>{r.value != null ? `${fmt(r.value)} ${r.units}` : r.result}{r.above_uloq && <small className="text-danger ml-1">&gt; ULOQ</small>}</td>
                                            <td>{r.cutoff != null ? `${fmt(r.cutoff)} ${r.units}` : '—'}</td>
                                            <td>{r.detected ? <span style={pill('#dc3545')}>DETECTED</span> : <span style={pill('#e9ecef', '#495057')}>Not detected</span>}</td>
                                            <td>{r.prescribed ? <span>Yes <small className="text-muted">({r.explained_by.join(', ')})</small></span> : 'No'}</td>
                                            <td>
                                                <span style={pill(r.outcome === 'INCONSISTENT' ? '#dc3545' : r.outcome === 'CONSISTENT' ? '#28a745' : '#6c757d')}>{r.outcome}</span>
                                                <div><small className="text-muted">{r.reason}</small></div>
                                            </td>
                                            <td>{r.detection_window || <span className="text-muted">—</span>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <small className="text-muted">Detection windows are approximate urine values and vary with dose, frequency and metabolism. This interpretation is a review aid; clinical correlation is required.</small>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <small className="text-muted mr-auto">Apply writes this interpretation to the patient report and regenerates the PDF.</small>
                <Button variant="primary" disabled={!data || applying || ['final', 'corrected', 'amended'].includes(data && data.report_status)} onClick={() => {
                    const again = data && data.applied;
                    Swal.fire({
                        title: again ? 'Re-apply to Report?' : 'Apply to Report?',
                        text: again ? 'This interpretation was already applied. Re-apply only if results or medications changed. The report PDF will be regenerated.' : 'Write this tox interpretation to the patient report and regenerate the PDF?',
                        icon: 'question', showCancelButton: true, confirmButtonText: again ? 'Yes, Re-apply' : 'Yes, Apply', cancelButtonText: 'Cancel',
                        customClass: { cancelButton: 'order-1', confirmButton: 'order-2' }
                    }).then((r) => {
                        if (!r.isConfirmed) return;
                        setApplying(true);
                        applyToxReview(orderId).then((res) => {
                            setApplying(false);
                            if (res && res.success && res.pdfOk) { toastr.success(res.message); if (onApplied) onApplied(); onClose(); return; }
                            if (res && res.success) { toastr.warning(res.message); if (onApplied) onApplied(); }
                            else toastr.error((res && res.message) || 'Apply failed');
                            getToxReview(orderId).then((x) => { if (x && x.status === 200) setData(x.data); });
                        }).catch(() => { setApplying(false); toastr.error('Apply failed'); });
                    });
                }}>{applying ? 'Applying…' : ['final', 'corrected', 'amended'].includes(data && data.report_status) ? 'Report is Final' : data && data.applied ? 'Re-apply to Report' : 'Apply to Report'}</Button>
                <Button variant="secondary" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}
